import React, { useState, useRef, useLayoutEffect } from "react";
import { motion } from "framer-motion";
import DropdownPortal from "../../../components/common/DropdownPortal";
import { MoreVertical, FilePen, Stethoscope, Clock10, UserX, Trash, BanknoteArrowDown, Check } from "lucide-react";
import { FaUserDoctor } from "react-icons/fa6";
import { arClass } from "../../../utils/useArClass";
import textTranslator from "../../../utils/useTextTranslator";
import Txt from "../../../components/Text";

const Text = ({ children, page = "WAITING_ROOM" }) => (
  <Txt page={page}>{children}</Txt>
);

// Reusable tooltip that anchors to its relative parent (the button) and
// dynamically flips above/below depending on available viewport space.
function ActionTooltip({ show, defaultLanguage, colorClasses, children }) {
  const [placement, setPlacement] = useState("bottom");
  const tooltipRef = useRef(null);

  useLayoutEffect(() => {
    if (!show || !tooltipRef.current) return;

    const el = tooltipRef.current;
    const parent = el.offsetParent || el.parentElement;
    if (!parent) return;

    const parentRect = parent.getBoundingClientRect();
    const tooltipHeight = el.offsetHeight || 60;
    const margin = 12; // breathing room from viewport edge

    const spaceBelow = window.innerHeight - parentRect.bottom;
    const spaceAbove = parentRect.top;

    // Prefer bottom; flip to top only if bottom doesn't fit and top has more room
    if (spaceBelow < tooltipHeight + margin && spaceAbove > spaceBelow) {
      setPlacement("top");
    } else {
      setPlacement("bottom");
    }
  }, [show]);

  if (!show) return null;

  return (
    <motion.span
      ref={tooltipRef}
      initial={{ opacity: 0, y: placement === "bottom" ? -5 : 5}}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ type: "tween", duration: 0.3, ease: "easeOut" }}
      className={arClass(`absolute z-50 left-3 -translate-x-1/2 whitespace-normal rounded-md text-xs px-3 py-2 break-words
        ${defaultLanguage === "fr" ? "!w-[280px] max-sm:!w-[280px]" : "!w-[260px] max-sm:!w-[220px]"}
        ${defaultLanguage === "ar" ? "text-right" : "text-left"}
        ${placement === "bottom" ? "top-full " : "bottom-full "}
        ${colorClasses}`)}
    >
      {children}
    </motion.span>
  );
}

export default function ActionsDropdown({ entry, actionsOpenId, anchorEl, onCloseActions, defaultLanguage, darkMode, theme, actionLoading, showActionDescription, setShowActionDescription, roles, isAdmin, handleStatusChange, handleDeleteEntry, toggleActions }) {
  return (
    <div className="flex items-center justify-center">
      <div className="relative">
        <button
          onClick={(e) => toggleActions(entry.id, e.currentTarget)}
          className={`p-2 rounded-md border transition-colors max-sm:p-1 ${
            darkMode ? "bg-slate-700 border-slate-600 hover:bg-slate-600" : "bg-transparent border-slate-300 hover:bg-slate-100"
          }`}
          title={textTranslator("Options", "WAITING_ROOM")}
        >
          <MoreVertical className="w-4 h-4 " />
        </button>

        {actionsOpenId === entry.id && anchorEl && (
          <DropdownPortal
            className={defaultLanguage === "fr" ? "!w-[320px] max-sm:!w-[300px]" : "!w-[280px] max-sm:!w-[240px]"}
            left={-200}
            anchorRef={{ current: anchorEl }}
            onClose={onCloseActions}
            animationProps={{
              initial: { opacity: 0, scale: 0.9, y: -10 },
              animate: { opacity: 1, scale: 1, y: 0 },
              exit: { opacity: 0, scale: 0.9, y: -10 },
              transition: { type: "spring", stiffness: 400, damping: 25, duration: 0.3 },
            }}
          >
            <motion.div initial="hidden" animate="visible" variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.05, delayChildren: 0.1 } } }}>

              {/* Start/Complete Triage */}
              <motion.button
                variants={{ hidden: { opacity: 0, x: -10 }, visible: { opacity: 1, x: 0 } }}
                whileHover={{ x: 4, zIndex: 1, scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => handleStatusChange(entry, entry.visitId, "with_nurse")}
                className={arClass(`w-full text-left px-3 py-2 flex items-center gap-2 disabled:cursor-not-allowed disabled:line-through relative ${theme.dropdownHover} max-sm:text-xs ${darkMode ? "text-green-400 disabled:text-green-400/50" : "text-green-800 disabled:text-green-800/50"} transition-all duration-200 rounded-sm`)}
                disabled={entry.status === "done" || (actionLoading.id === entry.id && actionLoading.type === "with_nurse")}
              >
                <ActionTooltip
                  show={showActionDescription.id === entry.id && showActionDescription.type === "with_nurse"}
                  defaultLanguage={defaultLanguage}
                  colorClasses={
                    entry.status === "done"
                      ? darkMode ? "bg-red-100 text-red-800" : "bg-red-100 text-red-900"
                      : darkMode ? "bg-green-100 text-green-800" : "bg-green-100 text-green-900"
                  }
                >
                  {entry.status === "done"
                    ? textTranslator("tooltipStartTriageWhenWithDoctor", "WAITING_ROOM")
                    : textTranslator("tooltipStartOrCompleteTriage", "WAITING_ROOM")}
                </ActionTooltip>
                <motion.div whileHover={{ rotate: 15, scale: 1.1 }} transition={{ type: "spring", stiffness: 300 }}>
                  {actionLoading.id === entry.id && actionLoading.type === "with_nurse" ? (
                    <motion.svg animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="w-4 h-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /></motion.svg>
                  ) : (
                    <FilePen className="w-4 h-4 sm:h-6 sm:w-6" />
                  )}
                </motion.div>
                <span className={arClass("relative min-w-max")} onMouseEnter={() => setShowActionDescription({ id: entry.id, type: "with_nurse" })} onMouseLeave={() => setShowActionDescription({ id: null, type: null })}>
                  <Text>start/completeVisitTriage</Text>
                </span>
              </motion.button>

              {(isAdmin || roles.includes("doctor")) && (
                <>
                  {/* Move to Nurse */}
                  <motion.button
                    variants={{ hidden: { opacity: 0, x: -10 }, visible: { opacity: 1, x: 0 } }}
                    whileHover={{ x: 4, zIndex: 1, scale: 1.02 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleStatusChange(entry, entry.visitId, "with_nurse")}
                    className={arClass(`w-full text-left px-3 py-2 text-blue-600 flex items-center gap-2 relative disabled:cursor-not-allowed disabled:line-through ${theme.dropdownHover} max-sm:text-xs ${darkMode ? "text-blue-400 disabled:text-blue-400/50" : "text-blue-800 disabled:text-blue-800/50"} transition-all duration-200 rounded-sm`)}
                    disabled={entry.status === "with_nurse" || entry.status === "waiting" || (actionLoading.id === entry.id && actionLoading.type === "with_nurse")}
                  >
                    <ActionTooltip
                      show={showActionDescription.id === entry.id && showActionDescription.type === "setPatientWithNurse"}
                      defaultLanguage={defaultLanguage}
                      colorClasses={
                        entry.status === "waiting" || entry.status === "with_nurse"
                          ? darkMode ? "bg-red-100 text-red-800" : "bg-red-100 text-red-900"
                          : darkMode ? "bg-blue-100 text-blue-800" : "bg-blue-100 text-blue-900"
                      }
                    >
                      {entry.status === "waiting" || entry.status === "with_nurse"
                        ? textTranslator("tooltipMoveToNurseBlocked", "WAITING_ROOM")
                        : textTranslator("tooltipMoveToNurse", "WAITING_ROOM")}
                    </ActionTooltip>
                    <motion.div whileHover={{ rotate: -15, scale: 1.2 }} transition={{ type: "spring", stiffness: 400 }}>
                      {actionLoading.id === entry.id && actionLoading.type === "with_nurse" ? (
                        <motion.svg animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="w-4 h-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /></motion.svg>
                      ) : (
                        <Stethoscope className="w-4 h-4 sm:w-6 sm:h-6" />
                      )}
                    </motion.div>
                    <span className={arClass("relative min-w-max")} onMouseEnter={() => setShowActionDescription({ id: entry.id, type: "setPatientWithNurse" })} onMouseLeave={() => setShowActionDescription({ id: null, type: null })}>
                      <Text>setPatientWithNurse</Text>
                    </span>
                  </motion.button>

                  {/* Move to Doctor */}
                  <motion.button
                    variants={{ hidden: { opacity: 0, x: -10 }, visible: { opacity: 1, x: 0 } }}
                    whileHover={{ x: 4, zIndex: 1, scale: 1.02 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleStatusChange(entry, entry.visitId, "with_doctor")}
                    className={arClass(`w-full text-left px-3 py-2 text-purple-600 flex items-center gap-2 relative disabled:cursor-not-allowed disabled:line-through ${theme.dropdownHover} max-sm:text-xs ${darkMode ? "text-purple-400 disabled:text-purple-400/50" : "text-purple-800 disabled:text-purple-800/50"} transition-all duration-200 rounded-sm`)}
                    disabled={entry.status === "waiting" || (actionLoading.id === entry.id && actionLoading.type === "with_doctor")}
                  >
                    <ActionTooltip
                      show={showActionDescription.id === entry.id && showActionDescription.type === "setPatientWithDoctor"}
                      defaultLanguage={defaultLanguage}
                      colorClasses={
                        entry.status === "waiting"
                          ? darkMode ? "bg-red-100 text-red-800" : "bg-red-100 text-red-900"
                          : darkMode ? "bg-purple-100 text-purple-800" : "bg-purple-100 text-purple-900"
                      }
                    >
                      {entry.status === "waiting"
                        ? textTranslator("tooltipMoveToDoctorBlocked", "WAITING_ROOM")
                        : textTranslator("tooltipMoveToDoctor", "WAITING_ROOM")}
                    </ActionTooltip>
                    <motion.div whileHover={{ rotate: -15, scale: 1.2 }} transition={{ type: "spring", stiffness: 400 }}>
                      {actionLoading.id === entry.id && actionLoading.type === "with_doctor" ? (
                        <motion.svg animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="w-4 h-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /></motion.svg>
                      ) : (
                        <FaUserDoctor className="w-4 h-4 sm:w-6 sm:h-6" />
                      )}
                    </motion.div>
                    <span className={arClass("relative min-w-max")} onMouseEnter={() => setShowActionDescription({ id: entry.id, type: "setPatientWithDoctor" })} onMouseLeave={() => setShowActionDescription({ id: null, type: null })}>
                      <Text>start/completeVisitMedical</Text>
                    </span>
                  </motion.button>
                </>
              )}

              {/* Reset to Waiting */}
              <motion.button
                variants={{ hidden: { opacity: 0, x: -10 }, visible: { opacity: 1, x: 0 } }}
                whileHover={{ x: 4, zIndex: 1, scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => handleStatusChange(entry, entry.visitId, "waiting")}
                className={arClass(`w-full text-left px-3 py-2 flex items-center gap-2 relative disabled:cursor-not-allowed disabled:line-through ${theme.dropdownHover} max-sm:text-xs ${darkMode ? " text-violet-300 disabled:text-violet-400/50" : " text-violet-900 disabled:text-violet-800/50"} transition-all duration-200 rounded-sm`)}
                disabled={entry.status === "waiting" || (actionLoading.id === entry.id && actionLoading.type === "waiting")}
              >
                <ActionTooltip
                  show={showActionDescription.id === entry.id && showActionDescription.type === "resetToWaiting"}
                  defaultLanguage={defaultLanguage}
                  colorClasses={
                    entry.status === "waiting"
                      ? darkMode ? "bg-red-100 text-red-800" : "bg-red-100 text-red-900"
                      : darkMode ? "bg-violet-100 text-violet-800" : "bg-violet-100 text-violet-900"
                  }
                >
                  {entry.status === "waiting"
                    ? textTranslator("tooltipResetToWaitingBlocked", "WAITING_ROOM")
                    : textTranslator("tooltipResetToWaiting", "WAITING_ROOM")}
                </ActionTooltip>
                <motion.div whileHover={{ rotate: 15, scale: 1.1 }} transition={{ type: "spring", stiffness: 300 }}>
                  {actionLoading.id === entry.id && actionLoading.type === "waiting" ? (
                    <motion.svg animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="w-4 h-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /></motion.svg>
                  ) : (
                    <Clock10 className="w-4 h-4 sm:w-6 sm:h-6" />
                  )}
                </motion.div>
                <span className={arClass("relative min-w-max")} onMouseEnter={() => setShowActionDescription({ id: entry.id, type: "resetToWaiting" })} onMouseLeave={() => setShowActionDescription({ id: null, type: null })}>
                  <Text>resetToWaiting</Text>
                </span>
              </motion.button>

              {/* Set Absent */}
              <motion.button
                variants={{ hidden: { opacity: 0, x: -10 }, visible: { opacity: 1, x: 0 } }}
                whileHover={{ x: 4, zIndex: 1, scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => handleStatusChange(entry, entry.visitId, "absent")}
                className={arClass(`w-full text-left px-3 py-2 flex items-center gap-2 relative disabled:cursor-not-allowed disabled:line-through ${theme.dropdownHover} max-sm:text-xs ${darkMode ? " text-orange-300 disabled:text-orange-300" : " text-orange-900 disabled:text-orange-900"} transition-all duration-200 rounded-sm`)}
                disabled={entry.status === "absent" || (actionLoading.id === entry.id && actionLoading.type === "absent")}
              >
                <ActionTooltip
                  show={showActionDescription.id === entry.id && showActionDescription.type === "setPatientAbsent"}
                  defaultLanguage={defaultLanguage}
                  colorClasses={
                    entry.status === "absent"
                      ? darkMode ? "bg-red-100 text-red-800" : "bg-red-100 text-red-900"
                      : darkMode ? "bg-orange-100 text-orange-800" : "bg-orange-100 text-orange-900"
                  }
                >
                  {entry.status === "absent"
                    ? textTranslator("tooltipSetAbsentBlocked", "WAITING_ROOM")
                    : textTranslator("tooltipSetAbsent", "WAITING_ROOM")}
                </ActionTooltip>
                <motion.div whileHover={{ rotate: 15, scale: 1.1 }} transition={{ type: "spring", stiffness: 300 }}>
                  {actionLoading.id === entry.id && actionLoading.type === "absent" ? (
                    <motion.svg animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="w-4 h-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /></motion.svg>
                  ) : (
                    <UserX className="w-4 h-4 sm:h-6 sm:w-6" />
                  )}
                </motion.div>
                <span className={arClass("relative min-w-max")} onMouseEnter={() => setShowActionDescription({ id: entry.id, type: "setPatientAbsent" })} onMouseLeave={() => setShowActionDescription({ id: null, type: null })}>
                  <Text>setPatientAbsent</Text>
                </span>
              </motion.button>

              {/* Delete Entry */}
              <motion.button
                variants={{ hidden: { opacity: 0, x: -10 }, visible: { opacity: 1, x: 0 } }}
                whileHover={{ x: 4, zIndex: 1, scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => handleDeleteEntry(entry)}
                className={arClass(`w-full text-left px-3 py-2 flex items-center gap-2 relative disabled:cursor-not-allowed disabled:!opacity-50 disabled:line-through ${theme.dropdownHover} max-sm:text-xs ${darkMode ? " text-red-400" : " text-red-900"} transition-all duration-200 rounded-sm`)}
                disabled={actionLoading.id === entry.id && actionLoading.type === "delete"}
              >
                <ActionTooltip
                  show={showActionDescription.id === entry.id && showActionDescription.type === "deleteEntry"}
                  defaultLanguage={defaultLanguage}
                  colorClasses={darkMode ? "bg-red-100 text-red-800" : "bg-red-100 text-red-900"}
                >
                  {textTranslator("tooltipDeleteEntry", "WAITING_ROOM")}
                </ActionTooltip>
                <motion.div whileHover={{ rotate: -15, scale: 1.1 }} transition={{ type: "spring", stiffness: 300 }}>
                  {actionLoading.id === entry.id && actionLoading.type === "delete" ? (
                    <motion.svg animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="w-4 h-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /></motion.svg>
                  ) : (
                    <Trash className="w-4 h-4 sm:w-6 sm:h-6" />
                  )}
                </motion.div>
                <span className={arClass("relative min-w-max")} onMouseEnter={() => setShowActionDescription({ id: entry.id, type: "deleteEntry" })} onMouseLeave={() => setShowActionDescription({ id: null, type: null })}>
                  <Text>deleteEntry</Text>
                </span>
              </motion.button>

            </motion.div>
          </DropdownPortal>
        )}
      </div>
    </div>
  );
}